import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileIcon from '../../assets/icons/profile.svg?react';
import type { WeekDateRangeResponse } from '../../api/generated';
import { Checkbox, Modal, WarningSign } from '../UI';
import { Button } from '../UI/base/Button';
import modalStyles from '../UI/Modal.module.css';
import type { GanttGroup } from './types';
import { collectEditableSeries } from '../../utils/ganttUtils';
import { RemovePlansScopePicker } from './RemovePlansScopePicker';
import { collectPatientsForPlans, type PlanPatient } from './planPatients';
import {
  collectAlreadyStartedWeeks,
  computePlansToRemove,
  countPlacedPlans,
  DEFAULT_REMOVE_PLANS_SCOPE,
  MIN_PLANS_FOR_SCOPE_CHOICE,
  type RemovalAnchor,
  type RemovePlansScope
} from './removePlansScope';
import styles from './RemoveTreatmentPlansModal.module.css';

const MODAL_WIDTH = 520;

interface RemoveTreatmentPlansModalProps {
  group: GanttGroup;
  /** Where the removal was launched from: one plan's menu, or the row's. */
  anchor: RemovalAnchor;
  /**
   * Fixes the removal to one scope and takes the picker away entirely, however
   * many plans the row holds. The row menu names the whole row before this
   * dialog opens, so offering to narrow it to a single plan afterwards would
   * answer a question nobody asked — and would let the row's own warning be
   * dodged with one click.
   */
  lockedScope?: RemovePlansScope;
  /** The year's week date ranges, used to tell which weeks have already started. */
  weeks: WeekDateRangeResponse[];
  patientsByPlanId: ReadonlyMap<string, PlanPatient[]>;
  onClose: () => void;
  onConfirm: (treatmentPlanIds: string[]) => void;
}

export const RemoveTreatmentPlansModal = ({
  group,
  anchor,
  lockedScope,
  weeks,
  patientsByPlanId,
  onClose,
  onConfirm
}: RemoveTreatmentPlansModalProps) => {
  const { t } = useTranslation();
  const acknowledgementId = useId();

  // Only offered when the caller left the scope open and the row holds several
  // placed plans. A single plan reaches this dialog when it would destroy
  // started weeks, and then there is a warning to read but no scope to pick.
  const canChooseScope =
    lockedScope === undefined &&
    countPlacedPlans(collectEditableSeries(group)) >= MIN_PLANS_FOR_SCOPE_CHOICE;
  const [chosenScope, setChosenScope] = useState<RemovePlansScope>(DEFAULT_REMOVE_PLANS_SCOPE);
  const scope = lockedScope ?? (canChooseScope ? chosenScope : 'plan');

  // The labels read the anchor week, which only a plan anchor has. A row anchor
  // cannot reach here anyway, since it arrives with its scope locked.
  const pickerAnchor = anchor.kind === 'plan' && canChooseScope ? anchor : null;

  // Which week set the planner acknowledged, not merely that they acknowledged
  // something. Widening the scope brings more history into range, so the tick has
  // to stop counting: it was given for a smaller loss than the one now pending.
  // Derived rather than reset in an effect, which this repo's lint rules forbid.
  const [acknowledgedWeeks, setAcknowledgedWeeks] = useState<string | null>(null);

  // Stable within the dialog's life: a `new Date()` inside the memo below would
  // make it impure and freeze at the first render anyway.
  const today = useMemo(() => new Date(), []);

  const { treatmentPlanIds, startWeeks, placedPlanCount } = useMemo(
    () => computePlansToRemove(group, anchor, scope),
    [group, anchor, scope]
  );

  const patients = useMemo(
    () => collectPatientsForPlans(treatmentPlanIds, patientsByPlanId),
    [treatmentPlanIds, patientsByPlanId]
  );

  // Recomputed per scope: narrowing to a future plan can take the started weeks
  // out of range entirely, and then none of this applies.
  const startedWeeks = useMemo(
    () => collectAlreadyStartedWeeks(group, treatmentPlanIds, weeks, today),
    [group, treatmentPlanIds, weeks, today]
  );

  const hasPlansToRemove = treatmentPlanIds.length > 0;
  const startedWeeksKey = startedWeeks.join(',');
  const isAcknowledged = acknowledgedWeeks === startedWeeksKey;
  // Destroying recorded history is not something to walk into on one click, so it
  // takes a deliberate second act. Scopes that touch only unstarted weeks are
  // unaffected: no warning, no extra step.
  const isDestroyingHistory = startedWeeks.length > 0;
  const canConfirm = hasPlansToRemove && (!isDestroyingHistory || isAcknowledged);

  // Reachable only if the row changed underneath an open dialog. Guarded rather
  // than assumed away: confirming would otherwise dispatch an empty removal and
  // the summary line would name a count of zero.
  const handleConfirm = (): void => {
    if (!canConfirm) return;
    onConfirm(treatmentPlanIds);
  };

  return (
    <Modal
      title={t('remove-plans-title')}
      onClose={onClose}
      onConfirm={canConfirm ? handleConfirm : undefined}
      width={MODAL_WIDTH}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose}>
            {t('delete-warning-cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} disabled={!canConfirm}>
            {t('delete-warning-confirm')}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        {pickerAnchor && (
          <RemovePlansScopePicker
            scope={scope}
            selectedStartWeek={pickerAnchor.selectedStartWeek}
            onChange={setChosenScope}
          />
        )}
        {/* Everything the scope decides is announced: the summarising sentences
            and the history warning with its acknowledgement. Widening the scope
            revokes a tick already given, and a silent re-arm leaves a screen
            reader user at a disabled confirm button with no reason for it.
            The name list stays outside: with real capacity it runs to dozens of
            people, and re-reading all of them on every scope change would bury
            the number that actually answers the question. */}
        <div aria-live="polite" aria-atomic="true" className={styles.outcome}>
          {/* Reachable when the row changed under an open dialog. Without this the
              dialog just goes inert, with a disabled button and no explanation. */}
          {!hasPlansToRemove && <p className={styles.summary}>{t('remove-plans-nothing-left')}</p>}
          {hasPlansToRemove && (
            <p className={styles.summary}>
              {t('remove-plans-summary', {
                count: placedPlanCount,
                weeks: startWeeks.join(', ')
              })}
            </p>
          )}
          {patients.length > 0 && (
            <p className={styles.patientsWarning}>
              {t('remove-plans-patients-warning', { count: patients.length })}
            </p>
          )}
          {isDestroyingHistory && (
            <div className={styles.historyWarning}>
              <WarningSign variant="error" />
              <div className={styles.historyWarningBody}>
                {/* Count and span, not the list: a mid-year removal routinely spans
                    a dozen weeks, and enumerating them reads as a wall of numbers
                    rather than as the scale of what is being destroyed. */}
                <p className={styles.historyWarningTitle}>
                  {t('remove-plans-history-title', {
                    count: startedWeeks.length,
                    first: startedWeeks[0],
                    last: startedWeeks[startedWeeks.length - 1]
                  })}
                </p>
                <p className={styles.historyWarningText}>{t('remove-plans-history-detail')}</p>
                <Checkbox
                  id={acknowledgementId}
                  isChecked={isAcknowledged}
                  onChange={(checked) => setAcknowledgedWeeks(checked ? startedWeeksKey : null)}
                  label={t('remove-plans-history-acknowledge')}
                />
              </div>
            </div>
          )}
        </div>
        {patients.length > 0 && (
          <ul className={styles.patientList}>
            {patients.map((patient) => (
              <li key={patient.patientId} className={styles.patientItem}>
                <ProfileIcon />
                {patient.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};
